import { affiliationService } from "app/services/affiliation.service";
import { displayResultMessage } from "utils/misc";
import { toFixed } from "@shared/utils/numbers";
import {
  CREATE_PAYOUT_MIN_AMOUNT,
  USD_PRECISION,
} from "features/affiliation/common/constants";
import { useState } from "react";
import { PayoutMethod, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useCreatePayoutRequest = (
  payoutMethods: PayoutMethod[] | undefined,
  closeModal: () => void,
  availableFunds?: number
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const [createPayout, { isLoading: createPayoutLoading }] =
    affiliationService.useCreatePayoutMutation();

  const availableFundsWithPrecision = toFixed(availableFunds, USD_PRECISION);
  const defaultPayoutMethodId = payoutMethods?.[0]?.payout_method_id;
  const [payoutAmount, setPayoutAmount] = useState(availableFundsWithPrecision);
  const [payoutMethodId, setPayoutMethodId] = useState(defaultPayoutMethodId);

  const minAmountError = payoutAmount < CREATE_PAYOUT_MIN_AMOUNT;
  const exceededAmountError = !!availableFunds && payoutAmount > availableFunds;
  const showAmountErrorMessage = minAmountError || exceededAmountError;

  const onCloseModal = () => {
    setPayoutAmount(availableFundsWithPrecision);
    setPayoutMethodId(defaultPayoutMethodId);
    closeModal();
  };

  const getMethodTypeById = (id: number) => {
    return payoutMethods?.find(
      (payoutMethod) => payoutMethod.payout_method_id === id
    )?.payout_method_type;
  };

  const createPayoutHandler = () => {
    const payoutMethodType = payoutMethodId
      ? getMethodTypeById(payoutMethodId)
      : undefined;
    if (payoutMethodType && payoutMethodId) {
      return displayResultMessage(
        createPayout({
          amount: payoutAmount,
          payout_method_id: payoutMethodId,
          payout_method_type: payoutMethodType,
        }).unwrap(),
        t("messages.payout_request_created"),
        t("messages.payout_request_create_failed")
      ).then(onCloseModal);
    }
  };

  const getAmountErrorMessage = () => {
    if (minAmountError) {
      return `Min amount: ${CREATE_PAYOUT_MIN_AMOUNT} USD`;
    } else if (exceededAmountError) {
      return `The selected amount is more than your available balance (${availableFundsWithPrecision} USD)`;
    }
  };

  return {
    createPayoutHandler,
    createPayoutLoading,
    showAmountErrorMessage,
    getAmountErrorMessage,
    payoutAmount,
    setPayoutAmount,
    payoutMethodId,
    setPayoutMethodId,
    availableFundsWithPrecision,
    onCloseModal,
  };
};

export default useCreatePayoutRequest;
